import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import FamilyTimeline from './components/FamilyTimeline';
import ChefProfiles from './components/ChefProfiles';
import IngredientSourcing from './components/IngredientSourcing';
import KebabArt from './components/KebabArt';
import CustomerStories from './components/CustomerStories';
import AwardsRecognition from './components/AwardsRecognition';
import SustainabilitySection from './components/SustainabilitySection';

const OurStory = () => {
  const [activeSection, setActiveSection] = useState('heritage');

  const sections = [
    { id: 'heritage', name: 'Our Heritage', icon: 'Heart' },
    { id: 'chefs', name: 'Meet Our Chefs', icon: 'ChefHat' },
    { id: 'sourcing', name: 'Ingredient Sourcing', icon: 'MapPin' },
    { id: 'art', name: 'Art of Kebab', icon: 'Flame' },
    { id: 'community', name: 'Community Stories', icon: 'Users' },
    { id: 'recognition', name: 'Recognition', icon: 'Award' },
    { id: 'sustainability', name: 'Sustainability', icon: 'Leaf' }
  ];

  const heroContent = {
    title: "Our Story",
    subtitle: "A Legacy of Authentic Mediterranean Flavors",
    description: `From the sun-kissed shores of the Mediterranean to your table, our story is one of passion, tradition, and unwavering commitment to authentic flavors. For over three decades, the Kebab Palace family has been sharing the rich culinary heritage passed down through generations, creating not just meals, but memories that bring people together.`,
    backgroundImage: "https://images.unsplash.com/photo-1544025162-d76694265947?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src={heroContent.backgroundImage}
            alt="Mediterranean kitchen heritage"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 via-primary-800/60 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-headline text-5xl md:text-7xl font-bold mb-6">
              {heroContent.title}
            </h1>
            <p className="font-accent text-xl md:text-2xl mb-8 text-accent-200">
              {heroContent.subtitle}
            </p>
            <p className="font-body text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-12">
              {heroContent.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/interactive-menu-culinary-discovery"
                className="btn-conversion inline-flex items-center space-x-2"
              >
                <Icon name="ChefHat" size={20} />
                <span>Explore Our Menu</span>
              </Link>
              <Link
                to="/order-online-streamlined-experience"
                className="btn-primary inline-flex items-center space-x-2"
              >
                <Icon name="ShoppingBag" size={20} />
                <span>Order Now</span>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <Icon name="ChevronDown" size={32} />
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="sticky top-20 z-40 bg-background/95 backdrop-blur-warm border-b border-primary-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex overflow-x-auto scrollbar-hide py-4 space-x-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-body font-medium whitespace-nowrap transition-organic ${
                  activeSection === section.id
                    ? 'bg-primary text-white shadow-warm'
                    : 'text-text-primary hover:text-primary hover:bg-primary-50'
                }`}
              >
                <Icon name={section.icon} size={18} />
                <span>{section.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        {activeSection === 'heritage' && <FamilyTimeline />}
        {activeSection === 'chefs' && <ChefProfiles />}
        {activeSection === 'sourcing' && <IngredientSourcing />}
        {activeSection === 'art' && <KebabArt />}
        {activeSection === 'community' && <CustomerStories />}
        {activeSection === 'recognition' && <AwardsRecognition />}
        {activeSection === 'sustainability' && <SustainabilitySection />}
      </div>

      {/* Call to Action Section */}
      <section className="bg-gradient-to-r from-primary-600 to-secondary-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="font-headline text-3xl md:text-4xl font-bold mb-6">
            Experience Our Story Through Every Bite
          </h2>
          <p className="font-body text-lg md:text-xl mb-8 opacity-90">
            Join our family tradition and taste the authentic flavors that have been perfected over generations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/order-online-streamlined-experience"
              className="bg-conversion hover:bg-conversion-600 text-white font-cta font-semibold px-8 py-4 rounded-lg shadow-warm transition-organic inline-flex items-center justify-center space-x-2"
            >
              <Icon name="ShoppingBag" size={20} />
              <span>Order Fresh Now</span>
            </Link>
            <Link
              to="/contact-location-welcoming-connection"
              className="bg-white/20 hover:bg-white/30 text-white font-cta font-semibold px-8 py-4 rounded-lg backdrop-blur-sm transition-organic inline-flex items-center justify-center space-x-2"
            >
              <Icon name="MapPin" size={20} />
              <span>Visit Our Restaurant</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurStory;